import axios from 'axios';

import { TOASTER_TYPE, ToastMessage } from '../../components';
import { apiHeaders, AxiosInstanceSetUp, getSessionData, urlConfig } from '../../config';
import { IUser } from '../../types/interfaces';
import { formatCart } from '../../utils/helper';

const handleError = () => {
  ToastMessage(TOASTER_TYPE.ERROR, "An error occured, Please try again");
};

export const initializeOpenSession: any = async (payload: any) => {
  let sessionData: any;

  try {
    sessionData = getSessionData();

    const config = {
      headers: {
        ...apiHeaders,
        "X-PYC-UserName": payload.name,
        "X-PYC-UserURN": payload.conURN,
      },
    };

    const res = await axios.post(
      `${urlConfig.BASE_URL}/wallets/${sessionData.tenant}/v1/Sessions`,
      {
        uniqueReferenceNumber: sessionData.uniqueReferenceNumber,
        merchantUniqueReferenceNumber:
          sessionData.merchantUniqueReferenceNumber,
        cart: formatCart(sessionData.cart), // convert cart price to kobo
        consumerUniqueReferenceNumber: payload.conURN,
        walletUniqueReferenceNumber: payload.wURN,
        shipping: sessionData.shipping,
        bookings: sessionData.bookings,
      },
      config,
    );

    return res;
  } catch (err: any) {
    if (err.response?.status === 500) handleError();
    return err;
  }
};

export const getSessionByUrn: any = async () => {
  let sessionData: any;

  try {
    sessionData = getSessionData();

    const axiosInstance = AxiosInstanceSetUp();

    const response = await axiosInstance({
      url: `/wallets/${sessionData.tenant}/v1/Sessions/${sessionData.uniqueReferenceNumber}`,
      method: "GET",
    });

    return response;
  } catch (err: any) {
    if (err.response?.status === 500) handleError();
    return err;
  }
};

export const updateOpenSession: any = async (payload: any) => {
  let sessionData: any;

  try {
    sessionData = getSessionData();

    const config = {
      headers: {
        ...apiHeaders,
        "X-PYC-UserName": sessionData.name,
        "X-PYC-UserURN": sessionData.conURN,
      },
    };

    const res = await axios.put(
      `${urlConfig.BASE_URL}/wallets/${sessionData.tenant}/v1/Sessions/${sessionData.uniqueReferenceNumber}`,
      {
        consumerUniqueReferenceNumber: payload.conURN,
        walletUniqueReferenceNumber: payload.wURN,
        merchantUniqueReferenceNumber:
          sessionData.merchantUniqueReferenceNumber,
        cart: formatCart(sessionData.cart), // Update cart for an existing session
        shipping: sessionData.shipping,
      },
      config,
    );

    return res;
  } catch (err: any) {
    if (err.response?.status === 500) handleError();
    return err;
  }
};

export const updateStoreCartItem: any = async (payload: any) => {
  let sessionData: any;

  try {
    sessionData = getSessionData();

    const axiosInstance = AxiosInstanceSetUp();

    const response = await axiosInstance({
      url: `/campaigns/${sessionData.tenant}/v1/StoreCartItems/${payload.uniqueReferenceNumber}`,
      method: "PATCH",
      data: payload,
    });

    return response;
  } catch (err: any) {
    if (err.response?.status === 500) handleError();
    return err;
  }
};

export const discardStoreCartItem: any = async (payload: any) => {
  let sessionData: any;

  try {
    sessionData = getSessionData();

    const axiosInstance = AxiosInstanceSetUp();

    const response = await axiosInstance({
      url: `/campaigns/${sessionData.tenant}/v1/StoreCartItems/${sessionData.uniqueReferenceNumber}`,
      method: "DELETE",
      data: {
        ...payload,
        consumerUniqueReferenceNumber: sessionData.conURN,
        merchantUniqueReferenceNumber:
          sessionData.merchantUniqueReferenceNumber,
      },
    });

    return response;
  } catch (err: any) {
    if (err.response?.status === 500) handleError();
    return err;
  }
};

// enroll for loyalty
export const enrollForLoyalty: any = async (payload: any) => {
  let sessionData: any;

  try {
    sessionData = getSessionData();

    const axiosInstance = AxiosInstanceSetUp();

    const response = await axiosInstance({
      url: `/wallets/${sessionData.tenant}/v1/Points`,
      method: "POST",
      data: payload,
    });

    return response;
  } catch (err: any) {
    if (err.response?.status === 500) handleError();
    return err;
  }
};

export const getAllEnrolledLoyalty: any = async (data: any) => {
  let sessionData: any;

  try {
    sessionData = getSessionData();

    const params = new URLSearchParams({
      CustomerUniqueReferenceNumber: sessionData.merchantUniqueReferenceNumber,
      MerchantUniqueReferenceNumber: sessionData.tenant,
      ConsumerUniqueReferenceNumber: sessionData.conURN,
      State: data.state,
    });

    const axiosInstance = AxiosInstanceSetUp();

    const response = await axiosInstance({
      url: `/loyalties/${sessionData.tenant}/v1/Members?${params}`,
      method: "GET",
    });

    return response;
  } catch (err: any) {
    if (err.response?.status === 500) handleError();
    return err;
  }
};

export const getAllRewards: any = async () => {
  let sessionData: any;

  try {
    sessionData = getSessionData();

    const config = {
      headers: {
        ...apiHeaders,
        "X-PYC-UserName": sessionData.name,
        "X-PYC-UserURN": sessionData.conURN,
      },
    };

    const paramData: any = {
      WalletUniqueReferenceNumber: sessionData.wURN,
      ConsumerUniqueReferenceNumber: sessionData.conURN,
      // ExpiryQuery: "NonExpired",
      PageSize: 50,
    };

    const params = new URLSearchParams();

    for (const key in paramData) {
      if (paramData.hasOwnProperty(key)) {
        params.set(key, paramData[key].toString());
      }
    }

    const res = await axios.get(
      `${urlConfig.BASE_URL}/wallets/${sessionData.tenant}/v1/Rewards?${params}`,
      config,
    );

    return res;
  } catch (err: any) {
    if (err.response?.status === 500) handleError();
    return err;
  }
};

export const getCartItemsInSession: any = async () => {
  let sessionData: any;

  try {
    sessionData = getSessionData();

    const config = {
      headers: apiHeaders,
    };

    const res = await axios.get(
      `${urlConfig.BASE_URL}/wallets/${sessionData.tenant}/v1/Sessions/${sessionData.uniqueReferenceNumber}`,
      config,
    );

    return res;
  } catch (err: any) {
    if (err.response?.status === 500) handleError();
    return err;
  }
};

// Transaction and Payment
export const initializeTransaction: any = async (payload: any) => {
  let sessionData: any;

  try {
    sessionData = getSessionData();
    const tenant = payload?.customerUniqueReferenceNumber || sessionData.tenant;

    const axiosInstance = AxiosInstanceSetUp();

    const res = await axiosInstance({
      url: `${urlConfig.BASE_URL}/payments/${tenant}/v1/Transactions`,
      data: {
        ...payload,
        paymentItemUniqueReferenceNumber:
          payload.paymentItemUniqueReferenceNumber ||
          sessionData.uniqueReferenceNumber,
        customerUniqueReferenceNumber:
          payload.customerUniqueReferenceNumber || sessionData.tenant,
      },
      method: "POST",
    });

    return res;
  } catch (err: any) {
    if (err.response?.status === 500) handleError();
    return err;
  }
};

export const verifyTransaction: any = async (payload: any) => {
  let sessionData: any;

  try {
    sessionData = getSessionData();
    const tenant =
      sessionData?.tenant || payload?.customerUniqueReferenceNumber;

    const axiosInstance = AxiosInstanceSetUp();

    const res = await axiosInstance({
      url: `${urlConfig.BASE_URL}/payments/${tenant}/v1/Transactions/${
        sessionData?.uniqueReferenceNumber || payload?.uniqueReferenceNumber
      }/verifications`,
      data: {
        ...payload,
      },
      method: "POST",
    });

    return res;
  } catch (err: any) {
    if (err.response?.status === 500) handleError();
    return err;
  }
};

export const getAllBanks = async (payload: {
  gateway: string;
  country: string;
  customerUniqueReferenceNumber: string;
}) => {
  let sessionData: any;

  try {
    sessionData = getSessionData();
    const tenant = payload.customerUniqueReferenceNumber || sessionData.tenant;

    const axiosInstance = AxiosInstanceSetUp();

    const res = await axiosInstance({
      url: `${urlConfig.BASE_URL}/payments/${tenant}/v1/Methods/${
        payload.gateway
      }/banks/${payload.country}?pageSize=${1}`,
      method: "GET",
    });

    return res;
  } catch (err: any) {
    if (err.response?.status === 500) handleError();
    return err;
  }
};

export const verifyAccountNumber = async (data: {
  gateway: string;
  accountNumber: string;
  bankCode: string;
  customerUniqueReferenceNumber: string;
}) => {
  let sessionData: any;

  try {
    sessionData = getSessionData();
    const tenant = data.customerUniqueReferenceNumber || sessionData.tenant;

    const axiosInstance = AxiosInstanceSetUp();

    const res = await axiosInstance({
      url: `${urlConfig.BASE_URL}/payments/${tenant}/v1/Methods/${data.gateway}/banks`,
      method: "POST",
      data,
    });

    return res;
  } catch (err: any) {
    if (err.response?.status === 500) handleError();
    return err;
  }
};

export const getMerchantDetails = async () => {
  let sessionData: any;

  try {
    sessionData = getSessionData();

    const axiosInstance = AxiosInstanceSetUp();

    const response = await axiosInstance({
      url: `Subscriptions/${urlConfig.API_TENANT}/v1/Customers/${sessionData.merchantUniqueReferenceNumber}`,
      method: "GET",
    });

    return response.data;
  } catch (err: any) {
    if (err.response?.status === 500) handleError();
    return err;
  }
};

export const getAdminDetails = async (data: {
  accountType: "Admin";
  PageSize: number;
  Page: number;
}) => {
  let sessionData: any;

  try {
    sessionData = getSessionData();

    const params = new URLSearchParams({
      PageSize: data.PageSize.toString(),
      Page: data.Page.toString(),
      AccountType: data.accountType ?? "",
    });

    const axiosInstance = AxiosInstanceSetUp();

    const response = await axiosInstance({
      url: `Subscriptions/${urlConfig.API_TENANT}/v1/Customers/?${params}`,
      method: "GET",
    });

    return response.data;
  } catch (err: any) {
    if (err.response?.status === 500) handleError();
    return err;
  }
};

export const getPaymentGateway: any = async (data: any) => {
  let sessionData: any;

  const headerDetails = data.queryKey ? data.queryKey[1] : data;

  try {
    sessionData = getSessionData();
    const token = localStorage.getItem("ID_T");

    const config = {
      headers: {
        ...apiHeaders,
        "X-PYC-UserName": sessionData.name || headerDetails.name,
        "X-PYC-UserURN":
          sessionData.conURN ||
          headerDetails.conURN ||
          headerDetails.consumerUniqueReferenceNumber,
        Authorization: `Bearer ${token}`,
      },
    };

    const res = await axios.get(
      `${urlConfig.BASE_URL}/payments/${sessionData.tenant ?? data.customerUniqueReferenceNumber}/v1/GatewaySettings`,
      config,
    );

    return res;
  } catch (err: any) {
    if (err.response?.status === 500) handleError();
    return err;
  }
};

export const createStripeSetupIntent: any = async (payload: any) => {
  let sessionData: any;

  try {
    sessionData = getSessionData();

    const marketplaceUrl = urlConfig.SITE_LINKS.marketplace;

    const res = await fetch(`${marketplaceUrl}/api/stripe/setup-intent`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        customerUniqueReferenceNumber:
          payload.customerUniqueReferenceNumber || sessionData.tenant,
        consumerUniqueReferenceNumber:
          payload.consumerUniqueReferenceNumber || sessionData.conURN,
        metadata: {
          customerUniqueReferenceNumber:
            payload.customerUniqueReferenceNumber || sessionData.tenant,
          consumerUniqueReferenceNumber:
            payload.consumerUniqueReferenceNumber || sessionData.conURN,
        },
        ...(payload.customerId && { customerId: payload.customerId }),
      }),
    });

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.error || "Stripe API error");
    }

    return data;
  } catch (err: any) {
    console.error("Stripe setup intent error:", err);
    return err;
  }
};

export const createStripeCustomer: any = async (payload: {
  email: string;
  name?: string;
  metadata?: Record<string, string>;
}) => {
  try {
    const marketplaceUrl = urlConfig.SITE_LINKS.marketplace;

    const res = await fetch(`${marketplaceUrl}/api/stripe/customers/create`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.error || "Stripe customer creation failed");
    }

    return data;
  } catch (err: any) {
    console.error("Stripe customer creation error:", err);
    return err;
  }
};

export const retrieveStripeCustomer: any = async ({
  customerUniqueReferenceNumber,
  consumerUniqueReferenceNumber,
}: {
  customerUniqueReferenceNumber: string;
  consumerUniqueReferenceNumber: string;
}) => {
  try {
    const marketplaceUrl = urlConfig.SITE_LINKS.marketplace;

    const res = await fetch(
      `${marketplaceUrl}/api/stripe/customers/search?customerUniqueReferenceNumber=${customerUniqueReferenceNumber}&consumerUniqueReferenceNumber=${consumerUniqueReferenceNumber}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (res.status === 404) {
      return null;
    }

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.error || "Stripe customer retrieval failed");
    }

    return data;
  } catch (err: any) {
    console.error("Stripe customer retrieval error:", err);
    return null;
  }
};

export const createCartToken: any = async (payload: any) => {
  let sessionData: any;

  try {
    sessionData = getSessionData();
    const tenant =
      sessionData?.tenant || payload?.customerUniqueReferenceNumber;

    const axiosInstance = AxiosInstanceSetUp();

    const res = await axiosInstance({
      url: `${urlConfig.BASE_URL}/wallets/${tenant}/v1/CardTokens`,
      data: {
        consumerUniqueReferenceNumber: sessionData?.conURN,
        walletUniqueReferenceNumber:
          sessionData?.wURN || payload?.walletUniqueReferenceNumber,
        ...payload,
      },
      method: "POST",
    });

    return res;
  } catch (err: any) {
    if (err.response?.status === 500) handleError();
    return err;
  }
};

export const selectDefaultCardToken: any = async (payload: any) => {
  const { uniqueReferenceNumber, ...data } = payload;
  let sessionData: any;

  try {
    sessionData = getSessionData();

    const config = {
      headers: {
        ...apiHeaders,
        "X-PYC-UserName": sessionData.name,
        "X-PYC-UserURN": sessionData.conURN,
      },
    };

    const res = await axios.put(
      `${urlConfig.BASE_URL}/wallets/${sessionData.tenant}/v1/CardTokens/${uniqueReferenceNumber}/defaults`,
      data,
      config,
    );

    return res;
  } catch (err: any) {
    if (err.response?.status === 500) handleError();
    return err;
  }
};

export const replaceCardToken: any = async (payload: any) => {
  let sessionData: any;

  try {
    sessionData = getSessionData();

    const config = {
      headers: {
        ...apiHeaders,
        "X-PYC-UserName": sessionData.name,
        "X-PYC-UserURN": sessionData.conURN,
      },
    };

    const res = await axios.put(
      `${urlConfig.BASE_URL}/wallets/${sessionData.tenant}/v1/CardTokens/${payload.uniqueReferenceNumber}`,
      {
        ...payload,
        consumerUniqueReferenceNumber: sessionData.conURN,
        walletUniqueReferenceNumber: sessionData.wURN,
        merchantUniqueReferenceNumber:
          sessionData.merchantUniqueReferenceNumber,
      },
      config,
    );

    return res;
  } catch (err: any) {
    if (err.response?.status === 500) handleError();
    return err;
  }
};

export const removeCardToken: any = async (payload: any) => {
  let sessionData: any;

  try {
    sessionData = getSessionData();

    const config = {
      headers: {
        ...apiHeaders,
        "X-PYC-UserName": sessionData.name,
        "X-PYC-UserURN": sessionData.conURN,
      },
    };

    const paramData = {
      WalletUniqueReferenceNumber: sessionData.wURN,
      GatewayCustomerId: payload.GatewayCustomerId,
    };

    const params = new URLSearchParams({
      ...paramData,
    });

    const res = await axios.delete(
      `${urlConfig.BASE_URL}/wallets/${sessionData.tenant}/v1/CardTokens/${payload.uniqueReferenceNumber}?${params}&CustomerEmailAddress=${payload.CustomerEmailAddress}&PaymentMethodUniqueReferenceNumber=${payload.PaymentMethodUniqueReferenceNumber}`,
      config,
    );

    return res;
  } catch (err: any) {
    if (err.response?.status === 500) handleError();
    return err;
  }
};

export const getCardToken: any = async (data: any) => {
  let sessionData: any;
  const tenant = data?.cusUrn || sessionData?.tenant;

  try {
    sessionData = getSessionData();

    const config = {
      headers: {
        ...apiHeaders,
        "X-PYC-UserName": sessionData?.name || data?.name,
        "X-PYC-UserURN": sessionData?.conURN || data?.conUrn,
      },
    };

    const paramData = {
      CustomerUniqueReferenceNumber: data?.cusUrn || sessionData?.tenant,
      MerchantUniqueReferenceNumber:
        data?.cusUrn || sessionData?.merchantUniqueReferenceNumber,
      ConsumerUniqueReferenceNumber: data?.conUrn || sessionData?.conURN,
      WalletUniqueReferenceNumber: data?.wUrn,
    };

    const params = new URLSearchParams({
      ...paramData,
    });

    const res = await axios.get(
      `${urlConfig.BASE_URL}/wallets/${tenant}/v1/CardTokens/?${params}`,
      config,
    );

    return res;
  } catch (err: any) {
    if (err.response?.status === 500) handleError();
    return err;
  }
};

export const getCardTokenWithUrn: any = async (data: any) => {
  let sessionData: any;

  const payload = data.queryKey[1];

  try {
    sessionData = getSessionData();

    const config = {
      headers: {
        ...apiHeaders,
        "X-PYC-UserName": sessionData.name || payload.name,
        "X-PYC-UserURN": sessionData.conURN || payload.conURN,
      },
    };

    const res = await axios.get(
      `${urlConfig.BASE_URL}/wallets/${sessionData.tenant}/v1/CardTokens/${payload.uniqueReferenceNumber}`,
      config,
    );

    return res;
  } catch (err: any) {
    if (err.response?.status === 500) handleError();
    return err;
  }
};

export const getEarnableRewards: any = async (data: {
  adminUniqueReferenceNumber: string;
}) => {
  let sessionData: any;

  try {
    sessionData = getSessionData();

    const config = {
      headers: {
        ...apiHeaders,
        "X-PYC-UserName": sessionData.name,
        "X-PYC-UserURN": sessionData.conURN,
      },
    };

    const body = {
      merchantUniqueReferenceNumber: sessionData.merchantUniqueReferenceNumber,
      consumerUniqueReferenceNumber: sessionData.conURN,
      customerUniqueReferenceNumber: sessionData.tenant,
      walletUniqueReferenceNumber: sessionData.wURN,
      cart: formatCart(sessionData.cart),
      ...data,
    };

    const res = await axios.post(
      `${urlConfig.BASE_URL}/wallets/${sessionData.tenant}/v1/Sessions/${sessionData.uniqueReferenceNumber}/rewards`,
      body,
      config,
    );

    return res;
  } catch (err: any) {
    if (err.response?.status === 500) handleError();
    return err;
  }
};

export const checkoutToPay: any = async (payload: any) => {
  let sessionData: any;

  try {
    sessionData = getSessionData();
    const token = localStorage.getItem("ID_T");

    const config = {
      headers: {
        ...apiHeaders,
        "X-PYC-UserName": sessionData.name,
        "X-PYC-UserURN": sessionData.conURN,
        Authorization: `Bearer ${token}`,
      },
    };

    const body = {
      ...payload,
      merchantUniqueReferenceNumber: sessionData.merchantUniqueReferenceNumber,
      consumerUniqueReferenceNumber: sessionData.conURN,
      walletUniqueReferenceNumber: sessionData.wURN,
      cart: formatCart(sessionData.cart),
    };

    const res = await axios.post(
      `${urlConfig.BASE_URL}/wallets/${sessionData.tenant}/v1/Sessions/${sessionData.uniqueReferenceNumber}/checkouts`,
      body,
      config,
    );

    return res;
  } catch (err: any) {
    if (err.response?.status === 500) handleError();
    return err;
  }
};

export const getPoints: any = async () => {
  let sessionData: any;

  try {
    sessionData = getSessionData();

    const config = {
      headers: {
        ...apiHeaders,
        "X-PYC-UserName": sessionData.name,
        "X-PYC-UserURN": sessionData.conURN,
      },
    };

    // const groupedByClause = ["loyaltyProgrammeMemberUniqueReferenceNumber", "loyaltyProgrammeTierUniqueReferenceNumber", "LoyaltyProgrammeUniqueReferenceNumber"]

    const paramData = {
      // CustomerUniqueReferenceNumber: sessionData.merchantUniqueReferenceNumber,
      // MerchantUniqueReferenceNumber: sessionData.tenant,
      ConsumerUniqueReferenceNumber: sessionData.conURN,
      GroupByClause: "LoyaltyProgrammeUniqueReferenceNumber", // merchantUniqueReferenceNumber, customerUniqueReferenceNumber
    };

    const params = new URLSearchParams(paramData);

    const res = await axios.get(
      `${urlConfig.BASE_URL}/loyalties/${sessionData.tenant}/v1/AccrualPointAggregation?${params}`,
      config,
    );

    return res;
  } catch (err: any) {
    if (err.response?.status === 500) handleError();
    return err;
  }
};

/** Book a demo */
export const bookADemo = async (data: any) => {
  try {
    const config = {
      headers: {
        ...apiHeaders,
        "X-PYC-UserName": "System",
        "X-PYC-UserURN": "System",
      },
    };

    const body = {
      ...data,
    };

    const res = await axios.post(
      `${urlConfig.BASE_URL}/SubScriptions/${urlConfig.PAYCURRA_ADMIN_URN}/v1/EnterprisePlanRequest`,
      body,
      config,
    );

    return res;
  } catch (err: any) {
    if (err.response?.status === 500) handleError();
    return err;
  }
};

export const getShippingTotalCharge = async (payload: any) => {
  let sessionData: any;

  try {
    sessionData = getSessionData();

    const data = {
      cartItems: sessionData.cart.map((item: any) => ({
        productCatalogueItemUniqueReferenceNumber: item.productURN,
        quantity: item.quantity,
        price: item.price,
      })),
      shippingDestination: sessionData.shipping,
      merchantUniqueReferenceNumber: sessionData.merchantUniqueReferenceNumber,
    };

    const axiosInstance = AxiosInstanceSetUp();

    const response = await axiosInstance({
      url: `/campaigns/${sessionData.tenant}/v1/Shipping/${sessionData.merchantUniqueReferenceNumber}/totalShippingCharges`,
      method: "POST",
      data,
    });

    return response;
  } catch (err: any) {
    if (err.response?.status === 500) handleError();
    return err;
  }
};

export const getUser = async (): Promise<IUser | null> => {
  let sessionData: any;

  try {
    sessionData = getSessionData();

    const axiosInstance = AxiosInstanceSetUp();

    const response = await axiosInstance({
      url: `/identities/${urlConfig.PAYCURRA_CONSUMER_CUSTOMER_URN}/v1/users/${sessionData.conURN}`,
      method: "GET",
    });

    return response.data;
  } catch (err: any) {
    if (err.response?.status === 500) handleError();
    return err;
  }
};

type TPayLinkStoreProfile = {
  uniqueReferenceNumber?: string;
  customerUniqueReferenceNumber: string;
  merchantUniqueReferenceNumber: string;
  merchantName?: string;
  slug?: string;
};

type TPayLinkStoreCartPayload = {
  uniqueReferenceNumber: string;
  cartItemSessionUniqueReferenceNumber: string;
  storeCartItemPathUniqueReferenceNumber?: string;
  consumerUniqueReferenceNumber: string;
  customerUniqueReferenceNumber: string;
  merchantUniqueReferenceNumber: string;
  productCatalogueItemUniqueReferenceNumber: string;
  productCategoryUniqueReferenceNumber: string;
  productVariantUniqueReferenceNumber: string;
  price: number;
  sku: string;
  quantity: number;
  productCatalogueItemName?: string;
  productVariantImageUrl?: string;
  isSavedItem?: boolean;
  cartItemAccessories?: any[];
};

export const getPayLinkStoreProfile = async (data: {
  slug?: string;
  merchantUniqueReferenceNumber?: string;
  customerUniqueReferenceNumber?: string;
}): Promise<TPayLinkStoreProfile | null> => {
  try {
    const params = new URLSearchParams({
      MerchantUniqueReferenceNumber: data.merchantUniqueReferenceNumber ?? "",
      CustomerUniqueReferenceNumber: data.customerUniqueReferenceNumber ?? "",
      Slug: data.slug ?? "",
      SortOrder: "StateAsc",
    });

    const axiosInstance = AxiosInstanceSetUp();

    const response = await axiosInstance({
      url: `/campaigns/${urlConfig.API_TENANT}/v1/StoreProfile?${params}`,
      method: "GET",
    });

    return response?.data?.data?.[0] ?? null;
  } catch (err: any) {
    if (err.response?.status === 500) handleError();
    throw err;
  }
};

export const getPayLinkStoreProducts = async (data: {
  customerUniqueReferenceNumber: string;
  merchantUniqueReferenceNumber: string;
  page?: number;
  pageSize?: number;
}): Promise<any[]> => {
  try {
    const params = new URLSearchParams({
      CustomerUniqueReferenceNumber: data.customerUniqueReferenceNumber,
      MerchantUniqueReferenceNumber: data.merchantUniqueReferenceNumber,
      State: "Live",
      isB2B: "false",
      Page: `${data.page ?? 1}`,
      PageSize: `${data.pageSize ?? 50}`,
    });

    const axiosInstance = AxiosInstanceSetUp();

    const response = await axiosInstance({
      url: `/campaigns/${urlConfig.API_TENANT}/v1/ProductCatalogueItemAggregations/catalogueItems?${params}`,
      method: "GET",
    });

    return response?.data?.data ?? [];
  } catch (err: any) {
    if (err.response?.status === 500) handleError();
    throw err;
  }
};

export const getPayLinkStoreCartItems = async (
  cartItemSessionUniqueReferenceNumber: string,
): Promise<any[]> => {
  try {
    const axiosInstance = AxiosInstanceSetUp();

    const response = await axiosInstance({
      url: `/campaigns/${urlConfig.API_TENANT}/v1/StoreCartItems?pageSize=50&State=Active&CartItemSessionUniqueReferenceNumber=${cartItemSessionUniqueReferenceNumber}`,
      method: "GET",
    });

    return response?.data?.data ?? [];
  } catch (err: any) {
    throw err;
  }
};

export const addPayLinkStoreCartItem = async (
  payload: TPayLinkStoreCartPayload,
) => {
  try {
    const axiosInstance = AxiosInstanceSetUp();

    const response = await axiosInstance({
      url: `/campaigns/${payload.merchantUniqueReferenceNumber}/v1/StoreCartItems`,
      method: "POST",
      data: payload,
    });

    return response;
  } catch (err: any) {
    if (err.response?.status === 500) handleError();
    throw err;
  }
};

export const amendPayLinkStoreCartItem = async (
  payload: TPayLinkStoreCartPayload,
) => {
  try {
    const axiosInstance = AxiosInstanceSetUp();
    const sessionUrn =
      localStorage.getItem("C_S") ||
      payload.cartItemSessionUniqueReferenceNumber;

    const response = await axiosInstance({
      url: `/campaigns/${payload.customerUniqueReferenceNumber}/v1/StoreCartItems/${sessionUrn}`,
      method: "PATCH",
      data: payload,
    });

    return response;
  } catch (err: any) {
    if (err.response?.status === 500) handleError();
    throw err;
  }
};

export const removePayLinkStoreCartItem = async (payload: {
  uniqueReferenceNumber: string;
  cartItemSessionUniqueReferenceNumber?: string;
  storeCartItemPathUniqueReferenceNumber?: string;
  consumerUniqueReferenceNumber: string;
  customerUniqueReferenceNumber: string;
  merchantUniqueReferenceNumber: string;
  productCatalogueItemUniqueReferenceNumber: string;
  productCatalogueVariantUniqueReferenceNumber: string;
}) => {
  try {
    const axiosInstance = AxiosInstanceSetUp();
    const storeCartItemPathUniqueReferenceNumber =
      payload.storeCartItemPathUniqueReferenceNumber ||
      payload.cartItemSessionUniqueReferenceNumber ||
      payload.uniqueReferenceNumber;

    const response = await axiosInstance({
      url: `/campaigns/${payload.customerUniqueReferenceNumber}/v1/StoreCartItems/${storeCartItemPathUniqueReferenceNumber}`,
      method: "DELETE",
      data: payload,
    });

    return response;
  } catch (err: any) {
    if (err.response?.status === 500) handleError();
    throw err;
  }
};
